import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { getCartItemCount } = useCart();

  const handleLearnMoreClick = () => {
    navigate('/request-a-demo');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleLogoClick = () => {
    navigate('/home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cartItemCount = getCartItemCount();

  return (
    <header
      className={`sticky top-0 z-50 w-full h-20 bg-[#FFFFFFF2] transition-all duration-300 ${
        isScrolled ? 'shadow-md' : ''
      }`}
    >
      <div className="container mx-auto h-full flex items-center justify-between px-4 md:px-8">
        {/* Logo */}
        <div className="flex items-center">
          <button
            className="p-0 m-0 border-none flex items-center bg-transparent focus:outline-none"
            onClick={handleLogoClick}
          >
            <img src="/images/ELIXIRX.webp" alt="Elixir Logo" className="h-24 w-24 rounded object-cover" />
          </button>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link
            to="/home"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-[#000000] hover:text-[#D4AF37] font-semibold text-base"
          >
            Home
          </Link>
          <Link
            to="/shop"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-[#000000] hover:text-[#D4AF37] font-semibold text-base"
          >
            Shop
          </Link>
          <Link
            to="/wellness"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-[#000000] hover:text-[#D4AF37] font-semibold text-base"
          >
            Wellness
          </Link>
          <Link
            to="/about"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-[#000000] hover:text-[#D4AF37] font-semibold text-base"
          >
            About
          </Link>
          <Link
            to="/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-[#000000] hover:text-[#D4AF37] font-semibold text-base"
          >
            Contact
          </Link>
          <Link
            to="/cart"
            className="hidden md:flex items-center text-yellow-500 hover:text-yellow-600 transition relative"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <ShoppingCart size={24} strokeWidth={2.5} />
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {cartItemCount}
              </span>
            )}
          </Link>
        </nav>

        {/* Mobile Hamburger and Cart */}
        <div className="md:hidden flex items-center gap-2">
          {/* Cart Icon (always visible on mobile) */}
          <Link
            to="/cart"
            className="relative flex items-center text-yellow-500 hover:text-yellow-600 transition"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Cart"
          >
            <ShoppingCart size={26} strokeWidth={2.5} />
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {cartItemCount}
              </span>
            )}
          </Link>
          {/* Hamburger */}
          <button
            onClick={toggleMenu}
            className="focus:outline-none bg-[#D4AF37] p-1 ml-2"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {menuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* CTA Button (Hidden on small screens) */}
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0f172a]/50 backdrop-blur-md p-4">
          <nav className="flex flex-col items-center space-y-6 ">
            <Link
              to="/home"
              className="text-white font-semibold text-base"
              onClick={() => setMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/shop"
              className="text-white font-semibold text-base"
              onClick={() => {
                setMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Shop
            </Link>
            <Link
              to="/wellness"
              className="text-white font-semibold text-base"
              onClick={() => {
                setMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Wellness
            </Link>

            <Link
              to="/about"
              className="text-white font-semibold text-base"
              onClick={() => {
                setMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-white font-semibold text-base"
              onClick={() => {
                setMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Contact
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
